import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT, isPlatformBrowser, ViewportScroller } from '@angular/common';
import type { OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject, Input, PLATFORM_ID } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { canonicalIdBase } from '@core-mkt/services/schema/schema-id.util';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import { VisionButtonComponent } from '@core-mkt/shared/components/vision-button/vision-button/vision-button.component';
import type { AccordionItem, AccordionSectionTitle } from './accordion';
import { Accordion } from './accordion';
import { getAccordionSchema } from './accordion.schema';

@Component({
  selector: 'accordion',
  templateUrl: './accordion.component.html',
  styleUrls: ['./accordion.component.scss'],
  standalone: true,
  imports: [CommonModule, VisionButtonComponent, LazyImgDirective],
})
export class AccordionComponent implements OnInit {
  @Input() data: Accordion;
  @Input() isChild = false;
  theme: ThemeInterface;
  accordionItems: AccordionItem[] = [];
  showLeftContent = true;
  showRightContent = false;
  closeIcon: SafeHtml | null;
  openIcon: SafeHtml | null;

  private readonly redactor = inject(WysiwygRedactorService);
  private readonly IconService = inject(IconService);
  private readonly themeParser = inject(ThemeParserService);
  private readonly schemaService = inject(SchemaService);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly breakpointObserver = inject(BreakpointObserver);

  get accordionType(): string {
    return this.data.accordionDisplayType === 'boxed' ? ' bg-white' : '';
  }

  get accordionTitle(): string {
    return this.data.accordionTitle;
  }

  get accordionLeftContent(): string {
    return this.data.accordionLeftContent;
  }

  get accordionRightContent(): string {
    return this.data.accordionRightContent;
  }

  get accordionPadding(): string {
    if (!this.isChild || (!this.showLeftContent && this.showLeftContent !== null)) {
      return 'py-v-spacing';
    }
    if (!this.isChild && this.showLeftContent === false) {
      return 'py-v-spacing max-w-vision mx-auto';
    }
    return '';
  }

  get buttonData(): VisionButton {
    return {
      bgTheme: '',
      buttonLink: this.data.buttonLink,
      buttonText: this.data.buttonLabel,
      buttonStyle: this.data.buttonType,
      newTab: this.data.buttonLinkNewTab,
      nofollow: this.data.buttonLinkNofollow,
      noopener: this.data.buttonLinkNewTab,
      noreferrer: this.data.buttonLinkNewTab,
      ctaTag: this.buttonCtaTag,
      fullWidth: false,
    };
  }

  get buttonType(): string {
    return this.data.buttonType;
  }

  get buttonLabel(): string {
    return this.data.buttonLabel;
  }

  get buttonCtaTag(): string {
    return this.data.buttonCtaTag !== null ? this.data.buttonCtaTag : '';
  }

  get buttonLink(): string {
    return this.data.buttonLink;
  }

  get buttonLinkNewTab(): boolean {
    return this.data.buttonLinkNewTab;
  }

  get buttonLinkNofollow(): boolean {
    return this.data.buttonLinkNofollow;
  }

  get accordionImageInArray(): boolean {
    return this.data.accordionItems.some((item: AccordionItem) => item.accordionImage?.[0]) ? true : false;
  }

  accordionImageURL(imgUrl: string): string {
    if (!imgUrl) {
      return '';
    }
    if (imgUrl.includes('auto=compress,format')) {
      return imgUrl;
    }
    return `${imgUrl}&auto=compress,format`;
  }

  ngOnInit(): void {
    this.schemaService.register(getAccordionSchema(this.data, canonicalIdBase(this.document.URL)));
    this.showLeftContent = this.data.accordionLeftContent && this.data.accordionLeftContent.length > 0;
    this.showRightContent = !this.showLeftContent && !!this.data.accordionRightContent?.length;
    this.data.accordionItems.forEach((item: AccordionItem) => {
      // `as string`: bypassSanitizer returns non-strings unchanged, so an already-sanitized SafeHtml passes through.
      item.itemDescription = this.redactor.bypassSanitizer(item.itemDescription as string);
      item.itemTitle = this.redactor.bypassSanitizer(item.itemTitle as string);
      item.accordionSectionTitle.forEach((title: AccordionSectionTitle) => {
        title.sectionTitle = this.redactor.bypassSanitizer(title.sectionTitle);
      });
      this.accordionItems.push(item);
    });
    this.data.accordionTitle = this.redactor.bypassSanitizer(this.data.accordionTitle);
    this.data.accordionLeftContent = this.redactor.bypassSanitizer(this.data.accordionLeftContent);
    if (this.data.accordionRightContent) {
      this.data.accordionRightContent = this.redactor.bypassSanitizer(this.data.accordionRightContent);
    }

    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      columnCount: 0,
      columnDistribution: '',
    };

    this.theme = this.themeParser.getThemeClasses(themeInput);

    // Validate if the accordion is using a dark background
    const isDarkBackground = this.theme?.root?.includes('bg-vis-dark');
    /**
     * If the accordion is using a dark background, change the icon color to white
     * Otherwise, keep the brand color
     */
    if (isDarkBackground) {
      this.openIcon = this.redactor.bypassSanitizer(
        this.IconService.getBrandIcon('accordion-open')?.icon.replace('fill-vis-brand-1', 'fill-white'),
      );
      this.closeIcon = this.redactor.bypassSanitizer(
        this.IconService.getBrandIcon('accordion-close')?.icon.replace('fill-vis-brand-1', 'fill-white'),
      );
    } else {
      this.openIcon = this.redactor.bypassSanitizer(this.IconService.getBrandIcon('accordion-open')?.icon);
      this.closeIcon = this.redactor.bypassSanitizer(this.IconService.getBrandIcon('accordion-close')?.icon);
    }
  }

  hasAccordionImage(accordionItem: AccordionItem): boolean {
    return accordionItem.accordionImage && accordionItem.accordionImage.length > 0;
  }

  /**
   * @param anchor The tapped item's title. On mobile it is held in place while the previously open item collapses.
   *   Without one, the item still toggles but nothing is held in place.
   */
  toggle(accordionItem: AccordionItem, anchor?: HTMLElement) {
    // Check if there's an image associated with the accordion
    // If there is an image, only 1 description and 1 image can be shown at a time
    if (this.hasAccordionImage(accordionItem)) {
      // If the clicked item is already showing its description, hide it + image
      if (accordionItem.showDescription) {
        accordionItem.showDescription = false;
      } else {
        // EPS-4968: below lg the image renders inside the open item, so collapsing an open item above the
        // tapped one pulls the tapped title up, often off-screen. Measure it now to undo that shift.
        const anchorTop = anchor && this.isMobileBrowser() ? anchor.getBoundingClientRect().top : null;
        // If the clicked item is not showing its description, hide all descriptions + images
        this.data.accordionItems.forEach((accordionItem: AccordionItem) => {
          accordionItem.showDescription = false;
        });
        // Show the description + image of the clicked item
        accordionItem.showDescription = true;
        if (anchorTop !== null) {
          this.keepAnchorInPlace(anchor, anchorTop);
        }
      }
    } else {
      // If there's no image associated with the accordion, simply toggle the visibility of the description
      accordionItem.showDescription = !accordionItem.showDescription;
    }
  }

  /**
   * Below Tailwind's `lg`, where the side image column is hidden and images render inline. The query must match
   * the brand presets' `lg` (1024px) that the template's `lg:hidden` uses, or the jump returns in the gap.
   */
  private isMobileBrowser(): boolean {
    return isPlatformBrowser(this.platformId) && !this.breakpointObserver.isMatched('(min-width: 1024px)');
  }

  /**
   * Renders the toggle now, then scrolls by however far the anchor moved, before the browser paints. Assumes the
   * expand and collapse are instant: a height transition on `.item-description` would make the second read land
   * mid-animation and bring the jump back.
   */
  private keepAnchorInPlace(anchor: HTMLElement, topBefore: number): void {
    this.cdr.detectChanges();
    const shift = anchor.getBoundingClientRect().top - topBefore;
    if (Math.abs(shift) < 1) return;
    const [x, y] = this.viewportScroller.getScrollPosition();
    // Instant, so the global `scroll-behavior: smooth` doesn't animate the correction.
    this.viewportScroller.scrollToPosition([x, y + shift], { behavior: 'instant' });
  }
}
